/**
 * State Insights Data
 * 
 * Contains heritage, culture, tourism, and cuisine information for Indian states.
 * Images should be placed in: src/assets/images/states_insight/{STATE_CODE}/
 */

export const StateInsights = {
  
  // Punjab
  PB: {
    heritage: [
      {
        type: 'text',
        content: `Punjab’s heritage is one of the most layered and historically significant in India. Situated at the crossroads of ancient trade routes, the region has witnessed the rise and fall of great civilizations, from the Indus Valley settlements to powerful medieval empires. Over centuries, Punjab evolved into a land known for resilience, spiritual depth, and cultural unity.`
      },

      {
        type: 'image',
        src: '/images/states_insight/PB/PB_heritage_1.webp',
        alt: 'Golden Temple, Amritsar'
      },

      {
        type: 'text',
        content: `At the heart of Punjab’s heritage stands the Golden Temple (Harmandir Sahib) in Amritsar — the spiritual center of Sikhism. Built on the principles of equality, humility, and selfless service, the temple welcomes people of all faiths. The sacred Sarovar (holy tank) surrounding it and the world’s largest community kitchen, Langar, reflect Punjab’s timeless values of compassion and inclusivity.`
      },

      {
        type: 'image',
        src: '/images/states_insight/PB/PB_heritage_2.webp',
        alt: 'Jallianwala Bagh Memorial'
      },

      {
        type: 'text',
        content: `Jallianwala Bagh stands as a solemn reminder of Punjab’s role in India’s freedom struggle. The tragic massacre of 1919 marked a turning point in the fight against colonial rule. Today, the preserved bullet marks, memorial flame, and Martyrs’ Well serve as powerful symbols of sacrifice, courage, and collective resistance.`
      },

      {
        type: 'image',
        src: '/images/states_insight/PB/PB_heritage_3.webp',
        alt: 'Virasat-e-Khalsa Museum, Anandpur Sahib'
      },

      {
        type: 'text',
        content: `Virasat-e-Khalsa at Anandpur Sahib represents Punjab’s heritage through a modern lens. This world-class museum narrates the journey of Sikhism, Punjab’s social evolution, and its enduring spirit using immersive galleries, art installations, and storytelling. Its striking architecture blends seamlessly with the surrounding landscape, symbolizing tradition meeting modernity.`
      },

      {
        type: 'image',
        src: '/images/states_insight/PB/PB_heritage_4.webp',
        alt: 'Partition Museum, Amritsar'
      },

      {
        type: 'text',
        content: `The Partition of 1947 deeply shaped Punjab’s modern identity. Millions were displaced, families were divided, and entire communities were transformed overnight. The Partition Museum in Amritsar preserves personal stories, letters, photographs, and oral histories, ensuring that this defining chapter of Punjab’s heritage is remembered with empathy and reflection.`
      }
    ],

    culture: [
      {
        type: 'text',
        content: `Punjab’s culture is a vibrant expression of joy, energy, and community life. Deeply rooted in agricultural traditions, it celebrates hard work, collective spirit, and an unbreakable bond with the land. Music, dance, festivals, and attire together form a culture that is both deeply traditional and globally admired.`
      },

      {
        type: 'image',
        src: '/images/states_insight/PB/PB_culture_1.webp',
        alt: 'Bhangra dance performance'
      },

      {
        type: 'text',
        content: `Bhangra and Giddha are the most iconic cultural expressions of Punjab. Originally performed to celebrate harvest seasons, Bhangra’s powerful movements and energetic beats symbolize strength and celebration, while Giddha reflects grace, storytelling, and the everyday lives of Punjabi women. These dance forms continue to represent Punjab on global stages.`
      },

      {
        type: 'image',
        src: '/images/states_insight/PB/PB_culture_2.webp',
        alt: 'Punjabi folk musicians performing'
      },

      {
        type: 'text',
        content: `Punjabi folk music is rich with emotion and rhythm, traditionally performed using instruments such as the dhol, tumbi, chimta, and algoza. Folk songs narrate stories of love, bravery, devotion, and rural life, forming an oral tradition that has been passed down through generations and continues to influence modern Punjabi music worldwide.`
      },

      {
        type: 'image',
        src: '/images/states_insight/PB/PB_culture_3.webp',
        alt: 'Punjabi traditional attire'
      },

      {
        type: 'text',
        content: `Traditional Punjabi attire reflects both practicality and vibrant aesthetics. Men often wear kurta-pajama with turbans, symbolizing dignity and identity, while women adorn colorful salwar-kameez paired with phulkari embroidery. These garments showcase Punjab’s love for bold colors, detailed craftsmanship, and cultural pride.`
      },

      {
        type: 'image',
        src: '/images/states_insight/PB/PB_culture_4.webp',
        alt: 'Lohri festival celebration'
      },

      {
        type: 'text',
        content: `Festivals form the heartbeat of Punjabi culture. Celebrations such as Lohri, Baisakhi, and Gurpurabs bring communities together through music, dance, bonfires, and shared meals. These festivals mark seasonal cycles, spiritual milestones, and historical events, reinforcing values of gratitude, unity, and joy.`
      }
    ],

    tourism: [
      {
        type: 'text',
        content: `Punjab offers a unique tourism experience that blends history, spirituality, patriotism, and rural charm. From sacred pilgrimage sites to historic landmarks and lively border ceremonies, the state welcomes visitors with warmth, pride, and unforgettable cultural experiences.`
      },

      {
        type: 'image',
        src: '/images/states_insight/PB/PB_tourism_1.webp',
        alt: 'Wagah Border ceremony'
      },

      {
        type: 'text',
        content: `The Wagah Border ceremony near Amritsar is one of Punjab’s most iconic tourist attractions. Held daily at sunset, the retreat ceremony showcases a dramatic display of discipline, patriotism, and coordinated military drills by Indian and Pakistani border forces. The electrifying atmosphere draws visitors from across the world.`
      },

      {
        type: 'image',
        src: '/images/states_insight/PB/PB_tourism_2.webp',
        alt: 'Anandpur Sahib town'
      },

      {
        type: 'text',
        content: `Anandpur Sahib is a major spiritual and historical destination, closely associated with the Sikh Gurus and the founding of the Khalsa. Surrounded by scenic hills and river views, the town attracts pilgrims and tourists alike, offering a peaceful blend of faith, history, and natural beauty.`
      },

      {
        type: 'image',
        src: '/images/states_insight/PB/PB_tourism_3.webp',
        alt: 'Rural Punjab village'
      },

      {
        type: 'text',
        content: `Rural tourism in Punjab provides an authentic glimpse into traditional village life. Visitors can experience mustard fields, tractor rides, folk performances, and local hospitality, gaining insight into Punjab’s agrarian roots and community-driven lifestyle.`
      },

      {
        type: 'image',
        src: '/images/states_insight/PB/PB_tourism_4.webp',
        alt: 'Bathinda Fort'
      },

      {
        type: 'text',
        content: `Historic forts and monuments such as Bathinda Fort reflect Punjab’s strategic importance through the centuries. These architectural landmarks narrate stories of ancient rulers, medieval conflicts, and the region’s role in shaping northern India’s history.`
      }
    ],

    cuisine: [
      {
        type: 'text',
        content: `Punjabi cuisine is celebrated for its bold flavors, generous portions, and comforting richness. Deeply influenced by the region’s agrarian lifestyle, the food reflects abundance, warmth, and a strong culture of sharing meals with family and guests. Dairy products, slow-cooked preparations, and robust spices define the heart of Punjabi cooking.`
      },

      {
        type: 'image',
        src: '/images/states_insight/PB/PB_cuisine_1.webp',
        alt: 'Sarson da Saag and Makki di Roti'
      },

      {
        type: 'text',
        content: `Sarson da Saag paired with Makki di Roti is the most iconic dish of Punjab. Prepared from mustard greens and served with white butter or ghee, it represents rural Punjab at its finest. Traditionally enjoyed during winter, this dish reflects the simplicity, nourishment, and seasonal rhythm of Punjabi life.`
      },

      {
        type: 'image',
        src: '/images/states_insight/PB/PB_cuisine_2.webp',
        alt: 'Punjabi street food'
      },

      {
        type: 'text',
        content: `Punjab’s street food scene is lively and indulgent. Favorites such as Amritsari kulcha with chole, paneer tikka, chaat, and stuffed parathas are enjoyed across bustling markets. These flavors showcase Punjab’s love for spice, texture, and freshly prepared food.`
      },

      {
        type: 'image',
        src: '/images/states_insight/PB/PB_cuisine_3.webp',
        alt: 'Punjabi sweets'
      },

      {
        type: 'text',
        content: `Traditional Punjabi sweets add a rich finish to festive meals. Delicacies like pinnis, jalebi, gajar ka halwa, and kheer are prepared during celebrations and winters, highlighting the generous use of ghee, jaggery, and milk that define Punjabi dessert traditions.`
      }
    ],
  },

  // Placeholder entries for other states (you can add content later)
  AN: { heritage: [], culture: [], tourism: [], cuisine: [] },
  AP: { heritage: [], culture: [], tourism: [], cuisine: [] },
  AR: { heritage: [], culture: [], tourism: [], cuisine: [] },
  AS: { heritage: [], culture: [], tourism: [], cuisine: [] },
  BR: { heritage: [], culture: [], tourism: [], cuisine: [] },
  CG: { heritage: [], culture: [], tourism: [], cuisine: [] },
  CH: { heritage: [], culture: [], tourism: [], cuisine: [] },
  DH: { heritage: [], culture: [], tourism: [], cuisine: [] },
  DL: { heritage: [], culture: [], tourism: [], cuisine: [] },
  GA: { heritage: [], culture: [], tourism: [], cuisine: [] },
  GJ: { heritage: [], culture: [], tourism: [], cuisine: [] },
  HP: { heritage: [], culture: [], tourism: [], cuisine: [] },
  HR: { heritage: [], culture: [], tourism: [], cuisine: [] },
  JK: { heritage: [], culture: [], tourism: [], cuisine: [] },
  JH: { heritage: [], culture: [], tourism: [], cuisine: [] },
  KA: { heritage: [], culture: [], tourism: [], cuisine: [] },
  KL: { heritage: [], culture: [], tourism: [], cuisine: [] },
  LA: { heritage: [], culture: [], tourism: [], cuisine: [] },
  LD: { heritage: [], culture: [], tourism: [], cuisine: [] },
  MP: { heritage: [], culture: [], tourism: [], cuisine: [] },
  MH: { heritage: [], culture: [], tourism: [], cuisine: [] },
  MN: { heritage: [], culture: [], tourism: [], cuisine: [] },
  ML: { heritage: [], culture: [], tourism: [], cuisine: [] },
  MZ: { heritage: [], culture: [], tourism: [], cuisine: [] },
  NL: { heritage: [], culture: [], tourism: [], cuisine: [] },
  OD: { heritage: [], culture: [], tourism: [], cuisine: [] },
  PY: { heritage: [], culture: [], tourism: [], cuisine: [] },
  RJ: { heritage: [], culture: [], tourism: [], cuisine: [] },
  SK: { heritage: [], culture: [], tourism: [], cuisine: [] },
  TN: { heritage: [], culture: [], tourism: [], cuisine: [] },
  TG: { heritage: [], culture: [], tourism: [], cuisine: [] },
  TR: { heritage: [], culture: [], tourism: [], cuisine: [] },
  UP: { heritage: [], culture: [], tourism: [], cuisine: [] },
  UK: { heritage: [], culture: [], tourism: [], cuisine: [] },
  WB: { heritage: [], culture: [], tourism: [], cuisine: [] },
};

export default StateInsights;
